import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { ApiService } from '../../services/apiservice/apiservice.service';
import { StorageService } from '../../services/storage/storage.service';
import { ConfigService } from '../../services/config/config.service';
import { HttpClient } from '@angular/common/http';
import { UserData, SYSTEMCATEGORY } from '../../services/auth/auth.service';
import { JacketArt, VersoHolding } from '../../core/interfaces/search.interface';
import { ApiResponse } from '../../services/api-response.interface';
import { UserSearchRequest } from '../staff/users/user.service';
import { BibMetadataModel } from '../../services/holdings/holdings.service';

export interface SearchPatron {
  matchedPatronCount: number;
  patrons: Patron[];
}

export interface patronIndicesList {
  code: string;
  displayName: string;
}

export interface Patron {
  address: string;
  barcode: string;
  email: string;
  firstName: string;
  fullName: string;
  homeLocation: number;
  initial: string;
  lastName: string;
  messages: string[];
  phone: string;
  photoUrl: string;
  userId: number;
}

export interface PickupLocation {
  pickupLocationID: number;
  pickupLocationName: string;
  homeLocationID: number;
  homeLocation: string;
  emailNotifyOption: string;
  emailSubject: string;
  emailAddress: string;
  emailOptions: string;
  isHomebound: boolean;
}

export type ReserveOutcome = 'Placed' | 'PlacedPendingReview' | 'NotPlaced';

export interface ReserveByTitleResult {
  agControlId: number;
  patronId: number;
  totalReservesPlaced: number;
  reserveResult: HoldingReserveResult[];
  patronName: string;
  pickupLocationName: string;
  status: ReserveOutcome;
  messages: string[];
}

export interface ReserveByTitleInfo {
  isTitleReserve: boolean;
  agControlId: number;
  patronId: number;
  pickupLocId: number;
  vol: string;
  notes: string;
  isSelfReserve: boolean;
  holdings: HoldingReserveDetail[];
  cancelIfNotFilledBy: string;
  patronName: string;
  pickupLocationName: string;
  notNeededBeforeDate: string;
  skipReserveCheck: boolean;
}

export interface HoldingReserveDetail {
  holdingsId: number;
  vol: string;
}

export interface HoldingReserveResult {
  holdingsId: number;
  reserveResult: boolean;
}

export interface ReserveItemsModel {
  pickupLocation: PickupLocation[];
  bibHoldings: any;
  reserveExpiresOn: string;
  patronHomeLocationId: number;
  canReserve: boolean;
  messages: string[];
  allowReserveAnyItem: boolean;
  reserveAnyItem: boolean;
  hasBlkReserveByCOLimit: boolean;
  bibMetadata: BibMetadataModel;
  titleAndAuthor: string;
  jacketArt: JacketArt;
}

export interface PlaceHoldRequest {
  agControlId: number;
  patronId: number;
}

@Injectable()
export class HoldService {
  private url = '';
  private _pat_hld_key = '_pat_hld';

  constructor(
    private _apiService: ApiService,
    private _storageService: StorageService,
    private _configService: ConfigService,
    private _http: HttpClient,
  ) {}

  setCachePatronHold(flag) {
    this._storageService.setItem(this._pat_hld_key, flag);
  }
  getCachePatronHold() {
    if (this._storageService.getItem(this._pat_hld_key) !== null) {
      return this._storageService.getItem(this._pat_hld_key);
    }
  }

  submitPlaceTitleReserve(data: ReserveByTitleInfo[]): Observable<ReserveByTitleResult[]> {
    this.url = `${this._configService.agApi}/ils/SubmitPlaceTitleReserve`;
    return this._apiService.post(this.url, data);
  }

  // Look up for a patron. Get patron Group and Index
  getPatronGroupAndIndex(): Observable<any> {
    this.url = `${this._configService.agApi}/ils/GetPatronGroupIndex`;
    return this._apiService.get(this.url);
  }

  /** Returns users based on agControlId for holds and requests*/
  searchPatrons(
    searchRequest: UserSearchRequest,
    agControlId: number = null,
  ): Observable<ApiResponse<SearchPatron>> {
    this.url = `${this._configService.agApi}/ils/SearchPatron`;
    return this._apiService.post(this.url, searchRequest, {
      ...(agControlId && { params: { agControlId } }),
    });
  }

  getHoldInfo(data): Observable<any> {
    this.url = `${this._configService.agApi}/ils/placeHold`;
    return this._apiService.post(this.url, data);
  }

  /**
   * Checks if patron is staff or has circOptions.allowSelfReserve and is not eContent
   * @param userData
   * @param isEContent
   * @returns
   */
  canPlaceHold(userData: UserData, isEContent: boolean) {
    return (
      userData.systemCategory == SYSTEMCATEGORY.VERSO &&
      (userData.isStaff ? true : userData.circOption.allowSelfReserve) &&
      !isEContent
    );
  }

  canDoPlaceHoldCill(holding: VersoHolding, isEContent: boolean, copies: number): boolean {
    const userData: UserData = this._storageService.getUserData();
    const patronBlockInfo: any = userData.patronBlockInfo;
    const showRequestThis = userData.illOptions.showRequestThis;

    // Can Self Reserve
    let canPlaceHold = this.canPlaceHold(userData, isEContent);
    // Is Versio CILL, Show Request Button, Not EContent
    let canPlaceIll =
      showRequestThis &&
      userData.systemCategory == SYSTEMCATEGORY.VERSO &&
      userData.illOptions.hasILL &&
      !isEContent;
    if (userData.isPatron && patronBlockInfo?.isBlockedPatron) {
      canPlaceHold = patronBlockInfo.canBlockedPatronReserveItem;
      canPlaceIll = patronBlockInfo.canBlockedPatronDoILL;
    }

    const canDoCill = holding?.candoCILL;
    return (
      canPlaceHold && !canDoCill && (!userData.libraryOptions.blockReserveIfNotOwned || copies > 0)
    );
  }

  /**
   * Returns true if user staff, or is patron or guest and circOptions.allowSelfReserve is true
   * @returns
   */
  allowPatronGuestReserve(): boolean {
    const userData = this._storageService.getUserData();
    if (userData.circOption && (userData.isPatron || userData.isGuest)) {
      return userData.circOption.allowSelfReserve;
    }
    return true;
  }
}
